import { Empty, Modal, Pagination, Spin } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  IBookCollectionData,
  IBookTypeData,
} from "../../apiservice/userService.type";
import { userGetBookGroup, userGetBooks } from "../../apiservice/userService";
import NoAudioBookFound from "../sharedcomponents/NoBookFoundInCategory";
import { LockOutlined } from "@ant-design/icons";

export const BooksCollectionsCardsComp = () => {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.groupId || "0";
  const title = params.groupName || "All Books";

  const [books, setBooks] = useState<IBookTypeData[]>([]);
  const [booksCollectionsData, setBooksCollectionsData] =
    useState<IBookCollectionData>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    fetchCollections();
    setCurrentPage(1); // Reset to first page when tag changes
  }, [params]);

  const fetchCollections = async () => {
    try {
      const bookCollectionsData = await userGetBookGroup(id);
      setBooksCollectionsData(bookCollectionsData?.data);
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      fetchBooks();
    }
  };

  const fetchBooks = async () => {
    try {
      const bookData =
        id === "0"
          ? await userGetBooks({ page: 1, pageSize })
          : await userGetBooks({
              bookGroupId: id || "",
              page: 1,
              pageSize,
            });

      if (bookData) {
        setBooks(bookData?.data);
        setTotalPage(bookData?.meta?.total);
      } else {
        setError("Books not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }

    setHasSearched(true);
    setCurrentPage(1);
  };

  const fetchBooksPage = async () => {
    try {
      const bookData =
        id === "0"
          ? await userGetBooks({ page: currentPage, pageSize })
          : await userGetBooks({
              bookGroupId: id || "",
              page: currentPage,
              pageSize,
            });

      if (bookData) {
        setBooks(bookData?.data);
        setTotalPage(bookData?.meta?.total);
      } else {
        setError("Books not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }

    setHasSearched(true);
  };

  useEffect(() => {
    if (hasSearched) {
      fetchBooksPage();
    }
  }, [currentPage]);

  if (loading) {
    // Default text color will be dark:text-stone-100 due to global styles
    return (
      <div className="p-4 text-center">
        {" "}
        <Spin size="large" /> Loading book details...
      </div>
    );
  }

  if (error || !books.length) {
    return (
      <div className="container mx-auto p-4">
        {error || <NoAudioBookFound></NoAudioBookFound>}
      </div>
    );
  }

  return (
    <div className="p-4 grid">
      {/* 📌 Collection Card */}
      {booksCollectionsData && id !== "0" && (
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6 p-4 rounded-2xl shadow-md bg-white dark:bg-stone-800">
          <img
            src={`${process.env.PUBLIC_URL + booksCollectionsData.imageUrl}`}
            alt={booksCollectionsData.title}
            className="w-32 h-32 object-cover rounded-lg"
          />
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-stone-100">
              {booksCollectionsData.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              {booksCollectionsData.description}
            </p>
            {/* <p className="text-sm text-gray-400 mt-1">
              Last updated:{" "}
              {new Date(booksCollectionsData.dateModified).toLocaleDateString()}
            </p> */}
          </div>
        </div>
      )}

      <h3 className="mb-4 font-bold text-lg text-gray-900 dark:text-stone-100">
        {books.length + " Books in this collection"}
      </h3>

      {books.length > 0 ? (
        <>
          {/* 📌 Books List View */}
          <div className="flex flex-col gap-4">
            {books.map((item, index: number) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 rounded-2xl shadow-md bg-white dark:bg-stone-800"
              >
                {/* Book Cover */}
                <div className="w-24 h-32 flex-shrink-0">
                  <img
                    src={`${process.env.PUBLIC_URL + item.coverImageUrl}`}
                    alt={item.title}
                    className="w-full h-full object-cover rounded-md"
                  />
                </div>

                {/* Book Details */}
                <div className="flex-1">
                  <Link
                    to={`/users/book-details/${item.id}/${item.title}`}
                    className="flex items-center gap-2 text-lg font-semibold text-blue-800 dark:text-blue-400 hover:underline"
                  >
                    {item.title}

                    {/* 🔒 Show Lock Icon if no access */}
                    {!item.hasAccess && (
                      <LockOutlined
                        style={{ fontSize: "20px", color: "red" }}
                      />
                    )}
                  </Link>

                  {/* <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Book ID: {item.id}
                  </p> */}
                </div>
              </div>
            ))}
          </div>

          {/* 📌 Pagination */}
          <div className="mt-4 flex justify-center">
            <Pagination
              current={currentPage}
              onChange={setCurrentPage}
              total={totalPage}
              pageSize={pageSize}
              hideOnSinglePage
            />
          </div>
        </>
      ) : (
        <Empty
          description={
            title
              ? `No books found under this collection "${title}"`
              : "Select a new Collection to see books"
          }
        />
      )}
    </div>
  );
};

export default BooksCollectionsCardsComp;
